// Move effects in the creatures' pixel style, drawn at screen coordinates: one look per move kind and one motion per
// element (flame rises, tide splashes, grove sways, storm crackles, stone drops, frost glints, shade pulls in, light
// flares). A copy of the hunt field's watch-mode effects (client/field.ts) with the camera taken out, so the battle
// stage (client/boardview.ts) can draw them anywhere. Call fxBegin with the context and pixel scale before drawing.
import { WAVE_RADIUS, type AnimKind } from '../shared/moves.ts';
import type { Element } from '../shared/elements.ts';
import { GLYPH } from './moveclock.ts';
import { liteMode } from './perf.ts';
import { EL_RAMP, particleSprite, sparkSprite, stamp } from './hitfx.ts';

/** One effect: kind, element, target (x, y) and origin (fx, fy) in canvas pixels, start time, seed. reach: a Wave's radius in source pixels. */
export interface MoveFx { kind: AnimKind; el: Element; x: number; y: number; fx: number; fy: number; seed: number; awk?: boolean; reach?: number }

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
let g: CanvasRenderingContext2D;
/** Canvas pixels per source pixel. */
let K = 2;
let dpr = 1;
export function fxBegin(ctx: CanvasRenderingContext2D, scale: number, ratio = 1) { g = ctx; K = scale; dpr = ratio; }
/** How long an effect of this kind runs (ms), and when it lands. */
export const fxDuration = (kind: AnimKind, awk = false) => MOVE_MS[kind] * (awk ? AWK_STRETCH : 1);
export const fxLand = (kind: AnimKind) => LAND_MS[kind] ?? 0;

const MOVE_MS: Record<AnimKind, number> = { jab: 320, slash: 380, projectile: 520, beam: 520, burst: 600, wave: 900, signature: 1250 };
/** Where the effect starts landing: a projectile's flight time, a Signature's pillar drop. */
const LAND_MS: Partial<Record<AnimKind, number>> = { projectile: 200, beam: 200, signature: 220 };
/** An Awakened move runs this much longer than a Signature, so its gilt rune circle has time to read. */
const AWK_STRETCH = 1.4;
/** The awakened gilt (art/rig3d/parts/awakened.ts GILT): shadow, mid, light, highlight. */
const GILT = ['#6a3810', '#e5a632', '#ffd468', '#fff3c0'];
/** Five runes, 3 x 5 source pixels each, for an Awakened move's circle (the sprites' rune halos in pixels). */
const RUNE_BITS = [
  ['.#.', '###', '.#.', '.#.', '.#.'],
  ['#.#', '###', '.#.', '.#.', '.#.'],
  ['#..', '##.', '#.#', '#..', '#..'],
  ['..#', '.#.', '#..', '.#.', '..#'],
  ['##.', '#.#', '#..', '#..', '#..'],
];

/** An element's mid, highlight and shadow from its creature-palette ramp (client/hitfx.ts). */
function tintOf(el: Element): [string, string, string] {
  const r = EL_RAMP[el];
  return [r[2], r[4], r[1]];
}

/** The impact star (client/hitfx.ts) over the first part of an effect, t0 = share of the effect it lasts. */
function spark(el: Element, cx: number, cy: number, t: number, t0: number) {
  if (t >= t0) return;
  const k = t / t0;
  g.globalAlpha = k < 0.67 ? 1 : (1 - k) * 3;
  stamp(g, sparkSprite(el, Math.floor(k * 3)), cx, cy, K);
}

/** A repeatable 0-1 value per effect, particle and channel. */
function rnd(seed: number, i: number, k: number) {
  const v = Math.sin(seed * 12.9898 + i * 78.233 + k * 37.719) * 43758.5453;
  return v - Math.floor(v);
}

function px(x: number, y: number, size: number, color: string) {
  const s = Math.max(1, Math.round(size * K));
  g.fillStyle = color;
  g.fillRect(Math.round(x - s / 2), Math.round(y - s / 2), s, s);
}

/** n particles bursting from (cx, cy) in screen pixels, moving as the element does. t runs 0 to 1. */
function particles(el: Element, seed: number, cx: number, cy: number, t: number, n: number, reach: number, size: number) {
  const [base, pale] = tintOf(el);
  const sprites: [number, number, boolean, boolean][] = [];
  // A slow device draws every other particle (client/perf.ts).
  const step = liteMode() ? 2 : 1;
  for (let i = 0; i < n; i += step) {
    const a = (i / n) * Math.PI * 2 + rnd(seed, i, 1) * 0.9;
    const sp = reach * (0.55 + rnd(seed, i, 2) * 0.6) * K;
    let dx = Math.cos(a) * sp * t, dy = Math.sin(a) * sp * t * 0.7;
    let sz = size * (rnd(seed, i, 3) > 0.55 ? 1.5 : 1);
    switch (el) {
      case 'flame': dy -= t * 14 * K; dx *= 0.6; sz *= 1 - t * 0.5; break;
      case 'tide': dy += (t * t * 26 - t * 10) * K; break;
      case 'grove': dx += Math.sin(t * 7 + i) * 3 * K; dy += t * 8 * K; break;
      case 'storm': dx += (rnd(seed, i + Math.floor(t * 8), 4) - 0.5) * 6 * K; dy += (rnd(seed, i + Math.floor(t * 8), 5) - 0.5) * 6 * K; break;
      case 'stone': dy += (t * t * 34 - t * 8) * K; sz *= 1.4; break;
      case 'frost': dx *= 0.7; dy = dy * 0.7 + t * 4 * K; if ((Math.floor(t * 10) + i) % 3 === 0) sz *= 1.6; break;
      case 'shade': dx = Math.cos(a) * sp * (1 - t); dy = Math.sin(a) * sp * (1 - t) * 0.7; break;
      case 'light': dx *= 1.3; dy *= 1.3; break;
    }
    // A few stay plain pixels for sparkle; the rest are tiny outlined sprites in the element's ramp.
    const odd = (i / step) % 2 === 1;
    if (i % 5 === 4) px(cx + dx, cy + dy, Math.min(sz, 1.4), odd ? pale : base);
    else sprites.push([cx + dx, cy + dy, sz >= 1.75 && !(el === 'shade' && t > 0.6), odd]);
  }
  // Every outline first, then every fill, so overlapping particles read as one outlined cluster.
  for (const [x, y, big, flip] of sprites) stamp(g, particleSprite(el, big, 'line'), x, y, K, flip);
  for (const [x, y, big, flip] of sprites) stamp(g, particleSprite(el, big, 'fill'), x, y, K, flip);
}

/** A ring of pixels, radius r in source pixels, flattened to the ground. */
function pixelRing(el: Element, cx: number, cy: number, r: number, n: number, size: number, color?: string) {
  const [base, pale] = tintOf(el);
  const step = liteMode() ? 2 : 1;
  for (let i = 0; i < n; i += step) {
    const a = (i / n) * Math.PI * 2;
    px(cx + Math.cos(a) * r * K, cy + Math.sin(a) * r * K * 0.55, size, color ?? ((i / step) % 2 ? base : pale));
  }
}

function drawGlyph(el: Element, cx: number, cy: number, scale: number, ink = '#10131f') {
  const rows = GLYPH[el];
  const s = Math.max(1, Math.round(scale * K));
  const [base, pale] = tintOf(el);
  for (let y = 0; y < 7; y++) for (let x = 0; x < 7; x++) {
    if (rows[y][x] !== '#') continue;
    g.fillStyle = ink;
    g.fillRect(Math.round(cx + (x - 3.5) * s) - 1, Math.round(cy + (y - 3.5) * s) - 1, s + 2, s + 2);
  }
  for (let y = 0; y < 7; y++) for (let x = 0; x < 7; x++) {
    if (rows[y][x] !== '#') continue;
    g.fillStyle = y < 3 ? pale : base;
    g.fillRect(Math.round(cx + (x - 3.5) * s), Math.round(cy + (y - 3.5) * s), s, s);
  }
}

/**
 * An Awakened move's circle: eight gilt runes standing on the ground round the impact, thrown out to their radius
 * at once, then turning slowly as the effect fades. Each rune is ink-outlined like the sprites.
 */
function runeCircle(cx: number, cy: number, t: number, seed: number) {
  const n = 8, rr = (10 + Math.min(1, t * 4) * 24) * K, spin = t * 1.1 + seed;
  const s = Math.max(1, Math.round(K));
  for (const under of [true, false]) for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + spin;
    const x = cx + Math.cos(a) * rr, y = cy + Math.sin(a) * rr * 0.55;
    const bits = RUNE_BITS[i % RUNE_BITS.length];
    for (let by = 0; by < 5; by++) for (let bx = 0; bx < 3; bx++) {
      if (bits[by][bx] !== '#') continue;
      const X = Math.round(x + (bx - 1.5) * s), Y = Math.round(y + (by - 5) * s);
      g.fillStyle = under ? '#2a1606' : by < 2 ? GILT[3] : GILT[2];
      if (under) g.fillRect(X - 1, Y - 1, s + 2, s + 2); else g.fillRect(X, Y, s, s);
    }
  }
}

/** Draws one move effect; false once it has finished. */
export function drawMove(e: MoveFx, age: number, W: number, H: number): boolean {
  const dur = MOVE_MS[e.kind] * (e.awk ? AWK_STRETCH : 1);
  if (age > dur) return false;
  if (age < 0) return true;
  const [base, pale, deep] = tintOf(e.el);
  const cx = e.x, cy = e.y - 10 * K;
  const land = LAND_MS[e.kind] ?? 0;
  const t = Math.max(0, (age - land) / (dur - land));
  const fade = 1 - t;
  switch (e.kind) {
    case 'jab':
      spark(e.el, cx, cy, t, 0.3);
      g.globalAlpha = fade;
      particles(e.el, e.seed, cx, cy, t, 6, 11, 1.2);
      break;
    case 'slash': {
      // Three pixel streaks wipe across the target, then the element's particles.
      const wipe = Math.min(1, age / 160);
      g.globalAlpha = age < 160 ? 1 : Math.max(0, 1 - (age - 160) / 200);
      // Dark under-strokes first, so the streaks carry the creatures' outline.
      for (const under of [true, false]) for (let s = -1; s <= 1; s++) {
        const steps = 9;
        for (let j = 0; j < steps * wipe; j++) {
          const k = j / (steps - 1);
          const sz = s === 0 ? 2 : 1.4;
          px(cx + (-10 + 20 * k + s * 4) * K, cy + (-9 + 18 * k - s * 2) * K, under ? sz + 1 : sz, under ? '#08070c' : j % 2 ? pale : base);
        }
      }
      g.globalAlpha = fade;
      particles(e.el, e.seed, cx, cy, t, 8, 14, 1.3);
      break;
    }
    case 'projectile':
    case 'beam': {
      if (age < land) {
        // An orb with a short trail flies from the lead to the target.
        const ox = e.fx, oy = e.fy - 10 * K;
        for (let j = 3; j >= 0; j--) {
          const k = Math.max(0, age / land - j * 0.09);
          g.globalAlpha = 1 - j * 0.22;
          stamp(g, particleSprite(e.el, j === 0), lerp(ox, cx, k), lerp(oy, cy, k) - Math.sin(k * Math.PI) * 6 * K, K);
        }
      } else {
        spark(e.el, cx, cy, t, 0.35);
        g.globalAlpha = fade;
        particles(e.el, e.seed, cx, cy, t, 10, 16, 1.5);
      }
      break;
    }
    case 'wave': {
      // A Wave: the ground ripples out from the target to the edge of its reach (WAVE_RADIUS tiles), three rings
      // apart, with the element's particles riding the front. Every wild the front passes takes the splash.
      const reach = e.reach ?? WAVE_RADIUS * 24;
      const front = 4 + Math.min(1, t * 1.25) * reach;
      // The ground under the front glows in the element's color, so the reach reads even in a still frame.
      g.globalAlpha = 0.22 * fade;
      g.fillStyle = base;
      g.beginPath(); g.ellipse(cx, cy + 6 * K, front * K, front * K * 0.55, 0, 0, Math.PI * 2); g.fill();
      spark(e.el, cx, cy + 4 * K, t, 0.25);
      for (let r = 0; r < 3; r++) {
        const k = t - r * 0.14;
        if (k <= 0) continue;
        const rad = 4 + Math.min(1, k * 1.25) * reach;
        g.globalAlpha = Math.max(0, (1 - k) * (r ? 0.75 : 1));
        pixelRing(e.el, cx, cy + 6 * K, rad, 22 + Math.round(rad * 1.1), r ? 1.8 : 2.8, r === 2 ? deep : undefined);
      }
      g.globalAlpha = fade;
      particles(e.el, e.seed, cx, cy + 4 * K, t, 22, reach * 0.9, 1.8);
      break;
    }
    case 'burst': {
      g.globalAlpha = fade;
      pixelRing(e.el, cx, cy + 6 * K, 6 + t * 22, 22, 1.8);
      spark(e.el, cx, cy, t, 0.35);
      g.globalAlpha = fade;
      particles(e.el, e.seed, cx, cy, t, 14, 22, 1.7);
      break;
    }
    case 'signature': {
      // A pillar of the element drops on the target; on impact a flash, rays and two rings spread over the
      // ground, the element's glyph rises, and the edges of the field glow in its color for a moment. Nothing
      // covers the scene and nothing waits on it. An Awakened move is the same, grander and in gilt: a gold sheath on
      // the pillar, a gilt-edged glyph, a gold edge glow and a circle of gilt runes round the impact.
      const awk = !!e.awk;
      if (age < land) {
        const k = age / land;
        g.globalAlpha = 0.85;
        if (awk) { g.fillStyle = GILT[2]; g.fillRect(Math.round(cx - 10 * K), 0, Math.round(20 * K), Math.round(cy * k)); }
        g.fillStyle = pale; g.fillRect(Math.round(cx - 7 * K), 0, Math.round(14 * K), Math.round(cy * k));
        g.fillStyle = base; g.fillRect(Math.round(cx - 3 * K), 0, Math.round(6 * K), Math.round(cy * k));
        break;
      }
      const edge = Math.max(0, 1 - t * 2.2);
      if (edge > 0) {
        g.globalAlpha = edge * (awk ? 0.5 : 0.4);
        const band = Math.round((awk ? 16 : 12) * dpr);
        g.fillStyle = awk ? GILT[1] : base;
        g.fillRect(0, 0, W, band); g.fillRect(0, H - band, W, band); g.fillRect(0, 0, band, H); g.fillRect(W - band, 0, band, H);
      }
      if (t < 0.3) {
        const k = 1 - t / 0.3;
        g.globalAlpha = k * 0.85;
        if (awk) { g.fillStyle = GILT[3]; g.fillRect(Math.round(cx - 10 * K * k), 0, Math.round(20 * K * k), Math.round(cy + 6 * K)); }
        g.fillStyle = pale; g.fillRect(Math.round(cx - 7 * K * k), 0, Math.round(14 * K * k), Math.round(cy + 6 * K));
        g.globalAlpha = k * 0.6;
        g.beginPath(); g.ellipse(cx, cy + 4 * K, (12 + t * 40) * K, (7 + t * 22) * K, 0, 0, Math.PI * 2); g.fill();
      }
      // Rays: twelve dotted spokes thrown out from the impact.
      g.globalAlpha = fade;
      for (let r = 0; r < 12; r++) {
        const a = (r / 12) * Math.PI * 2 + 0.13;
        for (let d = 6 + t * 18; d < 10 + t * 44; d += 4) px(cx + Math.cos(a) * d * K, cy + Math.sin(a) * d * K * 0.6, 1.6, r % 2 ? base : pale);
      }
      pixelRing(e.el, cx, cy + 6 * K, 10 + t * 42, 52, 2.6);
      pixelRing(e.el, cx, cy + 6 * K, 5 + t * 24, 34, 2, deep);
      if (awk) {
        pixelRing(e.el, cx, cy + 6 * K, 14 + t * 52, 64, 2.2, GILT[2]);
        g.globalAlpha = Math.min(1, fade * 2);
        runeCircle(cx, cy + 6 * K, t, e.seed);
        g.globalAlpha = fade;
      }
      particles(e.el, e.seed, cx, cy, t, awk ? 44 : 36, awk ? 44 : 36, 2.4);
      g.globalAlpha = Math.min(1, fade * 1.5);
      drawGlyph(e.el, cx, cy - (24 + t * 12) * K, awk ? 2.8 : 2.4, awk ? GILT[1] : undefined);
      break;
    }
  }
  g.globalAlpha = 1;
  return true;
}
